import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Stack, Typography,
} from '@mui/material';
import {
  ReplayOutlined as ReopenIcon,
  HourglassEmptyOutlined as SoonIcon,
} from '@mui/icons-material';

import HiringService, { HiringNotices } from '../../api/services/hiring.service';
import { useAuthStore } from '../../store/authStore';
import { refreshStalled } from './useStalled';
import { formatDate } from './hiringUI';

// What the silence rule did while nobody was looking.
//
// An automatic close that nobody is told about is a record quietly
// editing itself, so this is the other half of the rule: the first time
// anybody opens hiring after it has fired, the names are on screen with a
// Reopen beside each one. Undoing it takes one click, which is the test of
// whether an automatic action was allowed to be automatic.
//
// "Seen" is kept in this browser rather than on the server on purpose —
// it is one person's "I have read this", not a fact about the company,
// and the cost of seeing it twice on a second machine is a click.

const seenKey = (companyId: string | null | undefined) =>
  `toposds.hiring.notices.seen.${companyId || 'default'}`;

const AutoCloseNotice: React.FC = () => {
  const companyId = useAuthStore((s) => s.account?.companyId) as string | undefined;
  const [notices, setNotices] = useState<HiringNotices | null>(null);
  const [open, setOpen] = useState(false);
  const [reopened, setReopened] = useState<string[]>([]);

  useEffect(() => {
    let live = true;
    const since = (() => {
      try { return localStorage.getItem(seenKey(companyId)); } catch { return null; }
    })();

    HiringService.notices(since)
      .then((result) => {
        if (!live) return;
        setNotices(result);
        // Only ever interrupts for something that HAPPENED. A list of
        // people closing tomorrow is worth showing beside the closures,
        // and not worth a dialog of its own every morning.
        if (result.autoClosed.length) setOpen(true);
      })
      .catch(() => { /* Hiring is not every company's feature; stay quiet. */ });

    return () => { live = false; };
  }, [companyId]);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(seenKey(companyId), new Date().toISOString()); } catch { /* private mode */ }
    setOpen(false);
  }, [companyId]);

  const reopen = useCallback(async (id: string) => {
    await HiringService.reopen(id);
    setReopened((current) => [...current, id]);
    refreshStalled(true);
  }, []);

  if (!notices) return null;

  return (
    <Dialog open={open} onClose={dismiss} maxWidth="xs" fullWidth>
      <DialogTitle>
        {notices.autoClosed.length === 1
          ? 'One candidate closed itself'
          : `${notices.autoClosed.length} candidates closed themselves`}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Each of these was waiting on the candidate, and nothing came back. The record
          and the stage they reached are kept — reopen anybody you are still working.
        </Typography>

        <Stack divider={<Divider flexItem />}>
          {notices.autoClosed.map((row) => {
            const back = reopened.includes(row._id);
            return (
              <Stack
                key={row._id}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
                sx={{ py: 1 }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {row.name}
                    {row.num != null && (
                      <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.75 }}>
                        #{row.num}
                      </Typography>
                    )}
                  </Typography>
                  {/* What they owed us, not just how long they were
                      quiet: "14 days silent" sends somebody into the
                      record to find out what they were supposed to send. */}
                  <Typography variant="caption" color="text.secondary">
                    {row.reason || 'No reply'}
                  </Typography>
                </Box>
                {back ? (
                  <Chip size="small" label="Open again" color="primary" variant="outlined" />
                ) : (
                  <Button size="small" startIcon={<ReopenIcon />} onClick={() => reopen(row._id)}>
                    Reopen
                  </Button>
                )}
              </Stack>
            );
          })}
        </Stack>

        {/* The warning, where somebody can still act on it. */}
        {notices.closingSoon.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" alignItems="center" gap={0.75} sx={{ mb: 1 }}>
              <SoonIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              <Typography variant="subtitle2">Closing next</Typography>
            </Stack>
            {notices.closingSoon.map((row) => (
              <Typography key={row._id} variant="body2" color="text.secondary">
                {row.name} — {row.reason}, closes {formatDate(row.closesAt)}
              </Typography>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" onClick={dismiss}>Got it</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AutoCloseNotice;
